import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { displayPrice, formatDate } from '../lib/format';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const StatCard = ({ label, value, link, linkLabel, testid }) => (
  <div className="bg-white border border-[#D9D9D6] rounded-xl p-5" data-testid={testid}>
    <p className="text-xs font-semibold text-[#53565A]/70 uppercase tracking-wider">{label}</p>
    <p className="font-heading text-4xl text-[#24135F] mt-2">{value}</p>
    {link && (
      <Link to={link} className="text-xs text-[#E4002B] font-semibold hover:underline mt-1 inline-block">
        {linkLabel}
      </Link>
    )}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () => {
    api
      .get('/admin/stats')
      .then((res) => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/admin/vehicles/${deleteTarget.id}`);
      toast.success('Vehicle deleted');
      setDeleteTarget(null);
      load();
    } catch {
      toast.error('Failed to delete vehicle');
    }
  };

  if (loading || !stats) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <Skeleton className="h-64 rounded-xl" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="font-heading text-3xl tracking-wide text-[#24135F]">DASHBOARD</h1>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard testid="stat-total-vehicles" label="Total Vehicles" value={stats.total_vehicles} link="/admin/vehicles" linkLabel="View all vehicles" />
        <StatCard testid="stat-available-vehicles" label="Available Vehicles" value={stats.available} link="/admin/vehicles?status=Available" linkLabel="View available" />
        <StatCard testid="stat-sold-vehicles" label="Sold Vehicles" value={stats.sold} link="/admin/vehicles?status=Sold" linkLabel="View sold" />
        <StatCard testid="stat-total-enquiries" label="Total Enquiries" value={stats.enquiries} link="/admin/enquiries" linkLabel="View enquiries" />
      </div>

      <div className="bg-white border border-[#D9D9D6] rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#D9D9D6]/50">
          <h2 className="font-heading text-2xl tracking-wider text-[#24135F]">RECENT VEHICLES</h2>
          <Button
            asChild
            data-testid="admin-add-vehicle-button"
            className="rounded-full bg-[#E4002B] text-white hover:bg-[#E4002B]/90 h-9 px-4 text-xs font-bold tracking-wider transition-colors duration-150"
          >
            <Link to="/admin/vehicles/new">
              <Plus className="h-4 w-4 mr-1" /> ADD VEHICLE
            </Link>
          </Button>
        </div>
        <div className="overflow-x-auto">
          <Table data-testid="admin-recent-vehicles-table">
            <TableHeader>
              <TableRow>
                <TableHead>Vehicle</TableHead>
                <TableHead>Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stats.recent_vehicles.map((v) => (
                <TableRow key={v.id} data-testid="admin-vehicle-row" className="hover:bg-[#D9D9D6]/15">
                  <TableCell className="font-medium text-sm">{v.title}</TableCell>
                  <TableCell className="text-sm">{displayPrice(v)}</TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-2 text-sm">
                      <span className={`h-2 w-2 rounded-full ${v.status === 'Available' ? 'bg-[#24135F]' : 'bg-[#E4002B]'}`} />
                      {v.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm text-[#53565A]">{formatDate(v.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button asChild variant="ghost" size="icon" data-testid="admin-vehicle-edit-button" className="h-8 w-8 hover:bg-[#D9D9D6]/30">
                        <Link to={`/admin/vehicles/${v.id}/edit`}>
                          <Pencil className="h-4 w-4 text-[#53565A]" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        data-testid="admin-vehicle-delete-button"
                        onClick={() => setDeleteTarget(v)}
                        className="h-8 w-8 hover:bg-[#E4002B]/5"
                      >
                        <Trash2 className="h-4 w-4 text-[#E4002B]" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="delete-vehicle-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete vehicle?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.title}" and all its images. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-vehicle-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-vehicle-confirm-button"
              onClick={handleDelete}
              className="bg-[#E4002B] hover:bg-[#E4002B]/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
